import { Form } from "@/ui/form";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler } from "react-hook-form";
import { PiCheckBold } from "react-icons/pi";
import {
    AdvancedCheckbox,
    Button,
    CheckboxGroup,
    Input,
    Title,
} from "rizzui";
import { Role } from "@/types";
import { createRole, updateRole } from "@/server/api-services";
import { permissionsArray } from "./permissions";
import { useNavigate } from "react-router-dom";
import routes from "@/routers/routes";

interface RoleFormProps {
    register?: Role;
    isEdit: boolean;
}

export default function CreateEditRole({ register, isEdit }: RoleFormProps) {
    const navigate = useNavigate();
    const [reset, setReset] = useState({});
    const [isLoading, setLoading] = useState(false);

    useEffect(() => {
        if (register) {
            setReset({
                name: register.name || "",
                permission: register.permission,
            });
        }
    }, [register]);

    const onSubmit: SubmitHandler<Role> = async (data) => {
        setLoading(true);
        try {
            if (isEdit) {
                await updateRole(
                    register?.id ?? "",
                    data.name,
                    data.permission?.join(","),
                );
            } else {
                await createRole(data.name, data.permission?.join(","));
            }
            setReset({
                name: "",
                permission: [],
            });
            navigate(routes.userManagement.roles);
        } catch (error) {
            console.error("Failed to create role", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="flex justify-center border">
            <p>{register?.name}</p>
            <Form<Role>
                resetValues={reset}
                onSubmit={onSubmit}
                useFormProps={{
                    defaultValues: {
                        name: register?.name || "",
                        permission: register?.permission || [],
                    },
                }}
                // validationSchema={createRoleSchema}
                className="flex flex-grow flex-col gap-6 p-6 max-w-6xl @container [&_.rizzui-input-label]:font-medium [&_.rizzui-input-label]:text-gray-900"
            >
                {({ register, control, formState: { errors } }) => {
                    return (
                        <>
                            <Input
                                placeholder="Enter role name"
                                {...register("name")}
                                error={errors.name?.message}
                            />
                            <Title as="h6" className="font-medium">
                                Permissions
                            </Title>
                            <span className="text-sm text-gray-600">
                                Select the permissions for this role, you can
                                edit them later.
                            </span>

                            <Controller
                                name="permission"
                                control={control}
                                render={({ field: { onChange, value } }) => (
                                    <CheckboxGroup
                                        values={value || []}
                                        setValues={(newValues) => onChange(newValues)}
                                        className="grid grid-cols-3 gap-4"
                                    >
                                        {permissionsArray.map(
                                            ({ value: permissionValue, label }) => (
                                                <AdvancedCheckbox
                                                    key={permissionValue}
                                                    value={permissionValue}
                                                    inputClassName="[&:checked~span>.icon]:block"
                                                    contentClassName="flex items-center justify-center"
                                                    checked={value?.includes(permissionValue)} // Ensure checked state is managed
                                                    onChange={() => {
                                                        const newValues = value?.includes(permissionValue)
                                                            ? value.filter((v) => v !== permissionValue)
                                                            : [...(value || []), permissionValue];
                                                        onChange(newValues);
                                                    }}
                                                >
                                                    <PiCheckBold className="icon me-1 hidden h-[14px] w-[14px] md:h-4 md:w-4" />
                                                    <span className="font-medium">
                                                        {label}
                                                    </span>
                                                </AdvancedCheckbox>
                                            ),
                                        )}
                                    </CheckboxGroup>
                                )}
                            />

                            <div className="flex items-center justify-end gap-4">
                                <Button
                                    variant="outline"
                                    className="w-full @xl:w-auto"
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    isLoading={isLoading}
                                    className="w-full @xl:w-auto"
                                >
                                    {isEdit == true ? "Update Role" : "Create Role"}
                                </Button>
                            </div>
                        </>
                    );
                }}
            </Form>
        </div>
    );
}
